import {Service} from '../Service';
import {upsertInto} from '../../../common/helpers/db/upsertInto';
import {UpsertMode} from '../../../common/helpers/db/UpsertMode';
import {
    isRole,
    ParentRole,
    ParentRoleSearchResponse,
    PartialParentRole,
    PartialRole,
    Role,
    SearchRoleResponse,
    SortOrder
} from '../../rest-client/aat';
import {asNumber} from '../../../common/helpers/converters/asNumber';
import {asRxRoleArray} from '../../../common/model/db/types/RxRole';
import {ensure} from '../../../common/types/guards/ensure';

export class RolesService extends Service {
    async searchRolesByName(pattern: string): Promise<Array<PartialRole>> {
        const rolesApi = await this.services.aat.Roles;

        const response = await rolesApi.searchRoles({
            searchRoleRequest: {
                pattern: pattern,
                page: 0,
                pageSize: 100
            }
        });

        this.checkStatus(response);

        await this.updateDatabase(response.roles.map((role: PartialRole) => {
            return {
                id: role.id,
                name: role.name ?? '',
                notes: role.notes ?? '',
                enabled: true
            };
        }));

        return response.roles;
    }

    async searchRoleById(id: number): Promise<Role | undefined> {
        const rolesApi = await this.services.aat.Roles;

        try {
            const response = await rolesApi.getRole({id: id});

            this.checkStatus(response);
            await this.updateDatabase([response.role]);
            await this.updateDatabase(response.role.parentRoles);

            return response.role;
        } catch (error) {
            return undefined;
        }
    }

    async createRole(role: PartialRole): Promise<Role> {
        const rolesApi = await this.services.aat.Roles;

        const roleResponse = await rolesApi.createRole({
            roleRequest: {
                name: role.name ?? '',
                notes: role.notes ?? ''
            }
        });

        this.checkStatus(roleResponse);

        return roleResponse.role;
    }

    async updateRole(id: number, updatedRole: PartialRole): Promise<Role> {
        const rolesApi = await this.services.aat.Roles;
        const currentRole = await this.searchRoleById(id);

        ensure(isRole, currentRole);

        const roleResponse = await rolesApi.updateRole({
            id,
            updateRoleRequest: {
                parentRoles: currentRole.parentRoles,
                ...updatedRole
            }
        });

        this.checkStatus(roleResponse);

        return roleResponse.role;
    }

    async updateParentRoles(id: number, parentRoles: PartialParentRole[]): Promise<Role> {
        const rolesApi = await this.services.aat.Roles;
        const roleResponse = await rolesApi.updateRole({
            id,
            updateRoleRequest: {
                parentRoles
            }
        });

        this.checkStatus(roleResponse);

        return roleResponse.role;
    }

    async getRoleAsParentRole(id: string): Promise<PartialParentRole | undefined> {
        const role = await this.db.roles.getById$(id);
        if (role) {
            return ({
                id: asNumber(role.id),
                enabled: role.enabled
            });
        } else {
            return undefined;
        }
    }

    async searchRolesPaged(pattern: string, page: number, pageSize: number, excludeIds?: number[], sortBy?: string, sortOrder?: SortOrder): Promise<SearchRoleResponse> {
        const rolesApi = await this.services.aat.Roles;
        const response = await rolesApi.searchRoles({
            searchRoleRequest: {pattern, page, pageSize, filter: excludeIds, sortBy, sortOrder}
        });
        this.checkStatus(response);
        return response;
    }

    async searchParentRolesPaged(roleId: number, page: number, pageSize: number, sortBy?: string, sortOrder?: SortOrder): Promise<ParentRoleSearchResponse> {
        const rolesApi = await this.services.aat.Roles;
        const response = await rolesApi.searchRoleParentRoles({
            id: roleId,
            parentRoleSearchRequest: {page, pageSize, sortBy, sortOrder}
        });
        this.checkStatus(response);
        return response;
    }

    async searchParentRoles(roleId: number): Promise<ParentRoleSearchResponse> {
        const rolesApi = await this.services.aat.Roles;
        const response = await rolesApi.searchRoleParentRoles({
            id: roleId
        });
        this.checkStatus(response);
        return response;
    }

    async batchAddParentRoles(roleId: number, parentRoles: PartialParentRole[]): Promise<void> {
        const rolesApi = await this.services.aat.Roles;
        const response = await rolesApi.batchAddRoleParentRoles({
            id: roleId,
            parentRoleAssociationRequest: {parentRoles}
        });
        this.checkStatus(response);
    }

    async batchRemoveParentRoles(roleId: number, parentRoleIds: number[]): Promise<void> {
        const rolesApi = await this.services.aat.Roles;
        const response = await rolesApi.batchRemoveRoleParentRoles({
            id: roleId,
            parentRoleBatchRemoveRequest: {parentRoleIds}
        });
        this.checkStatus(response);
    }

    async batchUpdateParentRolesEnabled(roleId: number, parentRoleIds: number[], enabled: boolean): Promise<void> {
        const rolesApi = await this.services.aat.Roles;
        const response = await rolesApi.batchUpdateRoleParentRoles({
            id: roleId,
            parentRoleEnabledRequest: {parentRoleIds, enabled}
        });
        this.checkStatus(response);
    }

    async updateDatabase(roles: (Role | ParentRole | undefined)[]): Promise<void> {
        await upsertInto(
            this.db.roles,
            asRxRoleArray(roles),
            UpsertMode.REPLACE_OVERWRITE
        );
    }
}